/**
 * Typed TanStack Query hooks for the real SRAB Hono Worker.
 *
 * Phase 2.9 — Drop MSW, talk to the real BE at `VITE_API_BASE_URL`.
 *
 * Contract details live on the wiki page `API-Endpoints`. The summary:
 *   - `GET  /api/family/me`                → Family
 *   - `GET  /api/contracts`                → Contract[]
 *   - `GET  /api/recommendations`          → Recommendation[]
 *   - `POST /api/contracts/upload`         → ContractUploadResponse
 *                                            { contract, coverages, ocr }
 *   - `POST /api/contracts/:id/confirm`    → Contract (BE returns bare Contract,
 *                                            NOT the ContractConfirmResponse
 *                                            envelope from @srab/shared — see
 *                                            api/src/contracts/route.ts:268)
 *   - `POST /api/auth/photo-login`         → PhotoLoginResponse { session, user }
 *
 * 401-handling strategy:
 *   - The read hooks (family/contracts/recommendations) are session-scoped.
 *     A logged-out user gets `401` from the BE. Rather than letting that crash
 *     the scene, the hooks **swallow 401 → return null / empty array** so the
 *     UI renders as "not logged in" (HomeScene.tsx uses
 *     `!!useFamily().data` as `isLoggedIn`).
 *   - 404 on `/family/me` is also treated as null (family row gone — same
 *     UX outcome as never having logged in).
 *   - All other non-2xx still throw → React Query surfaces an error.
 *
 * Schema validation:
 *   - Every response is `.parse()`-d through the corresponding shared schema
 *     so a drifted BE shape blows up in dev rather than later in a component
 *     deep in the tree.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import {
  ContractSchema,
  ContractsResponseSchema,
  ContractUploadResponseSchema,
  FamilySchema,
  LoginSessionResponseSchema,
  LoginSessionStatusResponseSchema,
  PhotoLoginResponseSchema,
  RecommendationsResponseSchema,
  UploadSessionResponseSchema,
  type Contract,
  type ContractUploadResponse,
  type Family,
  type LoginSessionResponse,
  type LoginSessionStatusResponse,
  type PhotoLoginResponse,
  type Recommendation,
  type UploadSessionResponse,
} from '@shared/schemas'

import { ApiError, apiGet, apiPostJson, apiPostMultipart } from '@/lib/api'
import { deriveRecommendations } from '@/lib/derived-recommendations'

// ─── Query keys ────────────────────────────────────────────────────────────
export const queryKeys = {
  family: ['family', 'me'] as const,
  contracts: ['contracts'] as const,
  recommendations: ['recommendations'] as const,
}

// Helper — swallow auth-shaped statuses to a sensible "logged-out" default.
// The hook decides what that default looks like (null vs []), this just lets
// the queryFn fall through cleanly.
function isAuthFailure(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false
  return err.status === 401 || err.status === 404
}

// ─── Queries ───────────────────────────────────────────────────────────────
/**
 * "Who am I?" — returns the session's Family or `null` if no session.
 *
 * HomeScene reads `data` directly: `isLoggedIn = !!useFamily().data`.
 */
export function useFamily(): UseQueryResult<Family | null, Error> {
  return useQuery({
    queryKey: queryKeys.family,
    queryFn: async () => {
      try {
        return await apiGet<Family>('/api/family/me', FamilySchema)
      } catch (err) {
        if (isAuthFailure(err)) return null
        throw err
      }
    },
    // No session = empty; don't retry the 401 spam.
    retry: (failureCount, error) => {
      if (isAuthFailure(error)) return false
      return failureCount < 1
    },
  })
}

/**
 * The session's logged-in user, or `null` when logged out / pre-fetch.
 *
 * Thin convenience over `useFamily()` — reads `family.current_user` (added in
 * shared/src/schemas/family.ts). Lets HomeScene render the small username
 * chip without re-deriving the `null` cases (`undefined` data,
 * `data === null`, `current_user === null`).
 */
export function useCurrentUser(): Family['current_user'] {
  const familyQuery = useFamily()
  return familyQuery.data?.current_user ?? null
}

export function useContracts(): UseQueryResult<Contract[], Error> {
  return useQuery({
    queryKey: queryKeys.contracts,
    queryFn: async () => {
      try {
        return await apiGet<Contract[]>('/api/contracts', ContractsResponseSchema)
      } catch (err) {
        if (isAuthFailure(err)) return [] satisfies Contract[]
        throw err
      }
    },
    retry: (failureCount, error) => {
      if (isAuthFailure(error)) return false
      return failureCount < 1
    },
  })
}

/**
 * Recommendations — BE-first with a FE-derived fallback.
 *
 * The BE rule engine is tracked under issue #43 and currently returns `[]` for
 * every family. To keep the demo's recommendation strip useful in the
 * meantime, we **transparently substitute** a small, locally-computed set when
 * the BE comes back empty. As soon as the BE rule engine ships, the BE data
 * takes precedence — no code change here, no toggle, just a non-empty array.
 *
 * Order of precedence:
 *
 *   1. BE returns a non-empty `Recommendation[]` → use it as-is.
 *   2. BE returns `[]` (rule engine empty) → compute from
 *      `useContracts()` + `useFamily()` via `deriveRecommendations()`.
 *   3. BE returns 401/404 → still `[]` (consistent with no-session UX).
 *
 * The fallback is also visible to anyone consuming `useRecommendations()` —
 * which is what `RecommendationStack` (bottom-of-scene) AND
 * `UploadFlowSteps` (the new modal step 5) need.
 */
export function useRecommendations(): UseQueryResult<Recommendation[], Error> {
  const contractsQuery = useContracts()
  const familyQuery = useFamily()
  return useQuery({
    queryKey: queryKeys.recommendations,
    queryFn: async () => {
      try {
        return await apiGet<Recommendation[]>(
          '/api/recommendations',
          RecommendationsResponseSchema,
        )
      } catch (err) {
        if (isAuthFailure(err)) return [] satisfies Recommendation[]
        throw err
      }
    },
    retry: (failureCount, error) => {
      if (isAuthFailure(error)) return false
      return failureCount < 1
    },
    // `select` runs synchronously over the cached BE response on every render.
    // Branching on `length === 0` lets the FE fallback kick in only when the
    // BE truly has nothing to say. Once #43 lands and the BE rule engine
    // returns rows, this branch never fires and the BE data flows through
    // untouched.
    select: (beRecs): Recommendation[] => {
      if (beRecs.length > 0) return beRecs
      const contracts = contractsQuery.data ?? []
      const members = familyQuery.data?.members ?? []
      // Run the derivation even when both inputs are empty — R-007 (MTPL),
      // R-006 (no property), R-008 (travel) all fire on the all-empty
      // state, which keeps the bottom strip populated for a fresh visitor
      // who hasn't uploaded anything yet. The cards naturally shrink as
      // the user adds contracts.
      return deriveRecommendations({ contracts, members })
    },
  })
}

// ─── Mutations ─────────────────────────────────────────────────────────────
/**
 * Upload a contract photo to the real BE.
 *
 * The BE response is `ContractUploadResponse = { contract, coverages, ocr }`.
 * Callers most commonly need `contract.id` (for the subsequent confirm call)
 * and `ocr` (for the reveal step's preview). `coverages` is exposed for
 * completeness — `OcrPreview` doesn't currently render them.
 *
 * Anonymous-reachable: if no session cookie is present the BE auto-creates a
 * family + member + user + session in one batch and sets `Set-Cookie:
 * srab_session=…` on the response. The `family` query is invalidated
 * unconditionally on success so the scene flips from "not logged in" to
 * "logged in" on the very first upload.
 */
export function useUploadContract(): UseMutationResult<
  ContractUploadResponse,
  Error,
  FormData | Blob | File
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload) => {
      const form =
        payload instanceof FormData
          ? payload
          : (() => {
              const fd = new FormData()
              fd.append('file', payload)
              return fd
            })()
      const { data } = await apiPostMultipart<ContractUploadResponse>(
        '/api/contracts/upload',
        form,
        ContractUploadResponseSchema,
      )
      return data
    },
    onSuccess: () => {
      // Family may have been auto-created → re-fetch.
      qc.invalidateQueries({ queryKey: queryKeys.family })
      qc.invalidateQueries({ queryKey: queryKeys.contracts })
      qc.invalidateQueries({ queryKey: queryKeys.recommendations })
    },
  })
}

/**
 * Phone-side upload for the QR flow. Posts a contract photo to
 * `/api/contracts/upload?token=<tok>` — the token (not a cookie) carries the
 * family attribution, so this works from a phone that never logged in.
 *
 * Single-use: the BE marks the token used on first success, so a retry with
 * the same token returns `410`.
 */
export function usePhoneUpload(
  token: string,
): UseMutationResult<ContractUploadResponse, Error, File> {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const { data } = await apiPostMultipart<ContractUploadResponse>(
        `/api/contracts/upload?token=${encodeURIComponent(token)}`,
        form,
        ContractUploadResponseSchema,
      )
      return data
    },
  })
}

/** Phone calls this when the user taps "Hotovo" — signals the desktop to advance. */
export function usePhoneUploadFinish(token: string) {
  return useMutation({
    mutationFn: () =>
      apiPostJson(`/api/contracts/upload-finish?token=${encodeURIComponent(token)}`, undefined),
  })
}

/**
 * Desktop polls this to know when the phone has finished uploading all pages.
 * Returns `{ finished: true }` after the phone calls /upload-finish.
 * Query goes to error state (410) when the token expires unfinished.
 */
export function useUploadTokenStatus(
  token: string,
  enabled: boolean,
): UseQueryResult<{ finished: boolean }, Error> {
  return useQuery({
    queryKey: ['upload-token-status', token],
    queryFn: () =>
      apiGet<{ finished: boolean }>(
        `/api/contracts/upload-status?token=${encodeURIComponent(token)}`,
      ),
    refetchInterval: enabled ? 2500 : false,
    enabled: enabled && token.length > 0,
    gcTime: 0,
    retry: false,
  })
}

/**
 * Create a short-lived upload session for the QR phone-upload flow.
 *
 * Hits `POST /api/contracts/upload-session` (requires a desktop session) and
 * returns `{ upload_token, upload_url, expires_at }`. The desktop encodes a
 * frontend phone-upload page URL (built from `upload_token`) into a QR code;
 * the phone then POSTs the contract photo to
 * `/api/contracts/upload?token=<tok>` with no cookie.
 *
 * NOT auto-authenticated: an anonymous desktop gets `401`. Callers fall back
 * to the on-device DropZone (which keeps the anonymous first-upload story).
 */
export function useCreateUploadSession(): UseMutationResult<
  UploadSessionResponse,
  Error,
  void
> {
  return useMutation({
    mutationFn: async () =>
      apiPostJson<UploadSessionResponse>(
        '/api/contracts/upload-session',
        undefined,
        UploadSessionResponseSchema,
      ),
  })
}

/**
 * Confirm a `pending_confirm` contract. The BE returns the updated Contract
 * directly (not the wrapped ContractConfirmResponse — see the file-top notes).
 */
export function useConfirmContract(): UseMutationResult<Contract, Error, string> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (contractId: string) => {
      // POST with no body is fine — wrangler's Hono router only reads the
      // route params here.
      const res = await fetch(
        `${(import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8787'}/api/contracts/${encodeURIComponent(contractId)}/confirm`,
        {
          method: 'POST',
          credentials: 'include',
        },
      )
      if (!res.ok) {
        throw new ApiError(
          res.status,
          `/api/contracts/${contractId}/confirm`,
          `confirm_failed (${res.status})`,
        )
      }
      const json = (await res.json()) as unknown
      return ContractSchema.parse(json)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contracts })
      qc.invalidateQueries({ queryKey: queryKeys.recommendations })
    },
  })
}

/**
 * Photo-login. The BE response is `{ session, user }`. Cookie is set as a
 * side-effect; the FE doesn't need to read the token (HttpOnly anyway).
 *
 * On success we invalidate the family query so the scene's `isLoggedIn`
 * flips to true once `useFamily` re-fetches against the new cookie.
 */
export function usePhotoLogin(): UseMutationResult<
  PhotoLoginResponse,
  Error,
  FormData | Blob | File
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload) => {
      const form =
        payload instanceof FormData
          ? payload
          : (() => {
              const fd = new FormData()
              fd.append('photo', payload)
              return fd
            })()
      const { data } = await apiPostMultipart<PhotoLoginResponse>(
        '/api/auth/photo-login',
        form,
        PhotoLoginResponseSchema,
      )
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.family })
      qc.invalidateQueries({ queryKey: queryKeys.contracts })
      qc.invalidateQueries({ queryKey: queryKeys.recommendations })
    },
  })
}

/**
 * Create a short-lived login-session for the QR cross-device login flow.
 *
 * Anonymous (no cookie needed). Returns `{ login_token, login_url, expires_at }`.
 * The desktop encodes a frontend phone-login page URL into a QR; the phone
 * scans and POSTs a photo to `/api/auth/photo-login?login_token=<tok>`. The
 * desktop polls `useLoginSessionStatus(token)` to detect completion.
 */
export function useCreateLoginSession(): UseMutationResult<
  LoginSessionResponse,
  Error,
  void
> {
  return useMutation({
    mutationFn: async () =>
      apiPostJson<LoginSessionResponse>(
        '/api/auth/login-session',
        undefined,
        LoginSessionResponseSchema,
      ),
  })
}

/**
 * Desktop polling for the QR-login handoff. Returns `pending` while the phone
 * hasn't logged in yet, `ready` once it has (and the response carries the
 * `Set-Cookie: srab_session=…` that logs the desktop in), or `expired` when
 * the token's 10-minute window elapsed.
 *
 * Pollers should stop refetching as soon as `data.status !== 'pending'`.
 */
export function useLoginSessionStatus(
  token: string | null,
  options: { enabled?: boolean } = {},
): UseQueryResult<LoginSessionStatusResponse, Error> {
  const enabled = (options.enabled ?? true) && Boolean(token)
  return useQuery({
    queryKey: ['login-session', token],
    queryFn: async () => {
      try {
        return await apiGet<LoginSessionStatusResponse>(
          `/api/auth/login-session/${encodeURIComponent(token!)}`,
          LoginSessionStatusResponseSchema,
        )
      } catch (err) {
        // 410 from the BE is the "expired" terminal state — surface it as data
        // (not an error) so the UI can show a refresh affordance, matching the
        // contract-upload QR step.
        if (err instanceof ApiError && err.status === 410) {
          return { status: 'expired' } satisfies LoginSessionStatusResponse
        }
        throw err
      }
    },
    enabled,
    refetchInterval: (q) => {
      const status = q.state.data?.status
      if (!status || status === 'pending') return 2500
      return false
    },
    gcTime: 0,
  })
}

/**
 * Phone-side photo-login for the QR flow. POSTs the photo to
 * `/api/auth/photo-login?login_token=<tok>` — no cookie needed on the phone.
 * The BE stamps the login-token row with `used_at` + the new `session_id`;
 * the desktop's poll picks that up on the next tick.
 */
export function usePhonePhotoLogin(
  token: string,
): UseMutationResult<PhotoLoginResponse, Error, File> {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('photo', file)
      const { data } = await apiPostMultipart<PhotoLoginResponse>(
        `/api/auth/photo-login?login_token=${encodeURIComponent(token)}`,
        form,
        PhotoLoginResponseSchema,
      )
      return data
    },
  })
}
